import React, {useState, useRef, useEffect, useContext, useCallback, useMemo} from 'react';
import LetterBox from '../components/LetterBox';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import {
    enterCollectingMode,
    enterIdleMode,
    collectLetter,
    enterAnsweringMode,
    markWordAsFound,
    markLettersAsUsed,
} from '../actions/actions';
import {
    COLLECTING_GAME_MODE,
    IDLE_GAME_MODE,
    GAME_COMPLETED_MODE,
    EDITING_GAME_MODE,
    gap,
    ANSWERING_GAME_MODE,
} from '../constants';
import {getWordsState, getDirectionByCoordinates} from '../services/letters-service';
import GameOver from '../components/GameOver';

import './Board.scss';
import {StateContext} from '../App';

const propTypes = {
    words: PropTypes.array,
    enterCollectingMode: PropTypes.func,
    enterIdleMode: PropTypes.func,
    enterAnsweringMode: PropTypes.func,
    gameMode: PropTypes.string,
};

const throttle = (func, limit) => {
    let inThrottle;
    return function () {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
};

const Board = () => {
    const stateContext = useContext(StateContext);
    const collectedLetters = useSelector((state) => state.collectedLetters);
    const words = useSelector((state) => state.words);
    const gameMode = useSelector((state) => state.gameMode);
    const usedLetters = useSelector((state) => state.usedLetters);
    const [collectingDir, setCollectionDir] = useState(null);
    const [isBoardScrollable, setIsBoardScrollable] = useState(true);
    const wordsCount = useRef(0);
    const [flatLettersObjArray, setFlatLettersObjArray] = useState([]);
    const dispatch = useDispatch();
    const {isTouchDevice, wordColor} = stateContext;
    const longTapTimer = useRef(false);
    const [dimensions, setDimensions] = useState({rows: 0, cols: 0});

    /**
     * If the new words count is different then the count we store on the
     * component ref, we would like to calculate the words metrics again
     * and set the state of the flat letter object array
     */
    useEffect(() => {
        if (words.length !== wordsCount.current) {
            wordsCount.current = words.length;
            const {letters, cols, rows} = getWordsState(words);
            setDimensions({rows, cols});
            setFlatLettersObjArray(letters);
        }
    }, [words, wordsCount]);

    const onTouchStart = useCallback((event) => {
        if (!isTouchDevice) return;
        const touches = event.touches;
        if (touches.length === 1) {
            if (isBoardScrollable) {
                longTapTimer.current = setTimeout(() => {
                    // select letters
                    const letterBox = event.target;
                    setIsBoardScrollable(false);
                    startCollecting(letterBox);
                }, 500);
            }
        }
    });

    const onMouseDown = useCallback((event) => {
        if (isTouchDevice) return;
        const letterBox = event.target;
        startCollecting(letterBox);
    });

    function startCollecting(letterBox) {
        if (gameMode === IDLE_GAME_MODE) {
            if (!letterBox.classList.contains('letter-box')) return;

            dispatch(enterCollectingMode());
            const {xpos, ypos} = letterBox.dataset;
            const letterIndex = flatLettersObjArray.findIndex((item) => {
                return item.xPos === parseInt(xpos) && item.yPos === parseInt(ypos);
            });
            dispatch(collectLetter(flatLettersObjArray[letterIndex]));
        }
    }

    const onMouseUp = useCallback((event) => {
        clearTimeout(longTapTimer.current);
        setIsBoardScrollable(true);

        if (gameMode === COLLECTING_GAME_MODE) {
            let action;
            if (collectedLetters.length > 1) {
                setCollectionDir(null);
                action = enterAnsweringMode();
                dispatch(action);
                checkAnswer(collectedLetters);
            } else {
                action = enterIdleMode();
                dispatch(action);
            }
        }
    });

    const onMouseMove = (event) => {
        if (gameMode === COLLECTING_GAME_MODE) {
            let realTarget = event.target;
            if (event.type === 'touchmove') {
                const myLocation = event.changedTouches[0];
                realTarget = document.elementFromPoint(myLocation.clientX, myLocation.clientY);
            }

            const letterBox = realTarget;
            const classlist = letterBox?.classList;
            if (!classlist.contains('letter-box') || classlist.contains('collected')) return;
            const {xpos, ypos} = letterBox.dataset;

            if (collectedLetters.length) {
                // determine the direction the user is collecting words
                const previousCoordinate = collectedLetters[collectedLetters.length - 1];
                const currentCoordinate = {xPos: parseInt(xpos), yPos: parseInt(ypos)};
                const direction = getDirectionByCoordinates(previousCoordinate, currentCoordinate);

                if (!collectingDir) {
                    setCollectionDir(direction);
                } else if (direction !== collectingDir) {
                    return;
                }
            }

            const letterIndex = flatLettersObjArray.findIndex((item) => {
                return item.xPos === parseInt(xpos) && item.yPos === parseInt(ypos);
            });
            dispatch(collectLetter(flatLettersObjArray[letterIndex]));
        } else {
            if (event.type === 'touchmove') {
                clearTimeout(longTapTimer.current);
            }
        }
    };

    function checkAnswer(collectedLetters) {
        const answer = collectedLetters.map((letter) => letter.value).join('');
        const foundWord = words.find((word) => word.text === answer);
        if (foundWord) {
            dispatch(markWordAsFound(foundWord.text, wordColor));
            const letterIds = collectedLetters.map((letter) => ({id: letter.id, color: wordColor}));
            dispatch(markLettersAsUsed(letterIds));
            stateContext.pickNextWordColor();
        }
        dispatch(enterIdleMode());
    }

    const onScroll = useCallback(
        (event) => {
            if (gameMode === IDLE_GAME_MODE) {
                clearTimeout(longTapTimer.current);
            }
        },
        [gameMode]
    );

    const getIsCollected = useCallback(
        (id) => {
            return collectedLetters.findIndex((item) => item.id === id) > -1;
        },
        [collectedLetters]
    );

    const getBoardDim = useMemo(() => {
        return {
            width: `${gap * dimensions.cols - 6}px`,
            height: `${gap * dimensions.rows - 6}px`,
        };
    }, [dimensions]);

    return (
        <div
            className={`board ${isBoardScrollable ? 'scrollable' : 'collecting'}`}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            onMouseUp={onMouseUp}
            onTouchEnd={onMouseUp}
            onMouseMove={onMouseMove}
            onTouchMove={onMouseMove}
            onScroll={onScroll}
        >
            <div
                className={`letters-container ${gameMode === EDITING_GAME_MODE ? 'edit-mode' : ''} ${
                    gameMode === GAME_COMPLETED_MODE ? 'completed' : ''
                }`}
                style={getBoardDim}
            >
                {flatLettersObjArray.map((letter) => {
                    const usedLetter = usedLetters.find((usedLetter) => usedLetter.id === letter.id);

                    return (
                        <LetterBox
                            key={letter.id}
                            xpos={letter.xPos}
                            ypos={letter.yPos}
                            letter={letter.value}
                            collected={getIsCollected(letter.id)}
                            used={!!usedLetter}
                            color={usedLetter?.color}
                        ></LetterBox>
                    );
                })}
            </div>
        </div>
    );
};

Board.propTypes = propTypes;
export default Board;
